import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { ChevronLeftIcon, CopyIcon, XIcon } from 'lucide-react';
import { toast } from '@noor/lib';
import { Dialog, DialogContent, DialogTitle } from '@noor/ui';
import {
  useLegacyOrderDetail,
  useLegacyCourierTracking,
  legacyOrderApi,
  LegacyStatusBadge,
  LegacyImageLightbox,
  legacyDayMonthTime,
  findLegacyOrderStage,
} from '@/entities/legacy-order';
import { LegacyCheckAccess } from '@/entities/legacy-access';
import {
  toRoutePoints,
  pointsWithItems,
  isEditableStage,
  effectiveCourierTracking,
  legacyShortDate,
  legacyShortTime,
} from '../lib/order-detail';
import { useLegacyOrderRoute } from '../model/use-order-route';
import { LegacyOrderAddressInfo } from './legacy-order-address-info';
import { LegacyOrderOtherInfo } from './legacy-order-other-info';
import { LegacyOrderProducts } from './legacy-order-products';
import { LegacyOrderMiniMap } from './legacy-order-mini-map';
import { LegacyPriceFormula } from './legacy-price-formula';

/**
 * The order-detail drawer, reproducing the old panel's `OrderDrawer` +
 * `OrderSingleBox`.
 *
 * Composition, top to bottom: a header carrying the scheduled/created time,
 * the stage badge and copyable ids; then the route list, the summary cards,
 * the cargo manifest (only when something is being carried) and the price
 * cards on the left, with the route map alongside on wide screens. Expanding
 * the map replaces the header with the map header and fills the body with the
 * map alone (the source's `fullMap` mode).
 */
export function LegacyOrderDrawer({
  orderId,
  onClose,
}: {
  /** The open order, or null when closed. */
  orderId: number | null;
  onClose: () => void;
}) {
  /*
   * Nothing is rendered while closed — not even the Radix `Dialog` wrapper.
   *
   * Radix keeps a closing dialog mounted until its exit animation reports
   * `animationend`, and with the ymaps canvas inside, that animation's timeline
   * stalls: verified live, `data-state` flips to `closed` while the `exit`
   * animation stays `running` at `currentTime: 0` indefinitely, so the drawer
   * never unmounted and its overlay kept the whole page blocked. Unmounting at
   * the source removes the exit path entirely rather than trying to out-specify
   * the shared component's animation classes (a `duration-0` override does not
   * cancel the keyframes).
   *
   * The cost is no close animation on this one dialog, which is invisible next
   * to a drawer that would not close at all.
   *
   * Keyed by the order so route/lightbox/expanded state can never leak from
   * one order into another opened right after it.
   */
  if (orderId === null) return null;

  return <OrderDrawerBody key={orderId} orderId={orderId} onClose={onClose} />;
}

function OrderDrawerBody({ orderId, onClose }: { orderId: number; onClose: () => void }) {
  const { t, i18n } = useTranslation('legacy');
  const navigate = useNavigate();
  const { data: order, isLoading, isError } = useLegacyOrderDetail(orderId);

  /**
   * The map expanded over the whole body, hiding the info column — the
   * source's `fullMap` mode. A drawer-level concern rather than the map's own
   * state, because it changes what ELSE renders — including the header.
   */
  const [mapExpanded, setMapExpanded] = useState(false);

  /**
   * The receipt lightbox. Lives at the drawer root (the source lifted
   * `setUploadedImages` all the way to the page for the same overlay); the
   * summary cards only ask for it to open.
   */
  const [lightboxImages, setLightboxImages] = useState<string[] | null>(null);

  const points = useMemo(() => (order ? toRoutePoints(order) : []), [order]);
  const cargoPoints = useMemo(() => (order ? pointsWithItems(order) : []), [order]);

  // The courier's live position — polled every 5s while a courier is assigned
  // and the order has not reached stage 15 (the hook's own gating).
  const { data: tracking } = useLegacyCourierTracking({
    orderId,
    courierId: order?.courier?.id ?? null,
    stage: order?.stage ?? 0,
  });

  /*
   * The fix the map draws: the live poll while it carries one, else the
   * order's own last-reported `courier.location`. The poll alone leaves the
   * map courier-less whenever it errors or answers without a `location`,
   * even though the order response already carries a usable fix.
   */
  const effectiveTracking = useMemo(
    () => effectiveCourierTracking(tracking ?? null, order?.courier?.location ?? null),
    [tracking, order?.courier?.location],
  );

  const { route, courierPath } = useLegacyOrderRoute({
    points,
    tracking: effectiveTracking,
  });

  /*
   * Offer subscription for a stage-6 order (`OrderSingleBox.tsx:130-151`):
   * subscribe on open so the dispatcher pushes offers for it, unsubscribe on
   * close AND on a tab unload. The `beforeunload` preventDefault/returnValue
   * pair is the source's own — it holds the tab long enough for the
   * unsubscribe to leave.
   */
  const stage = order?.stage;
  useEffect(() => {
    if (stage !== 6) return;

    void legacyOrderApi.subscribeToOrder(orderId);

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      void legacyOrderApi.unsubscribeToOrder(orderId);
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      void legacyOrderApi.unsubscribeToOrder(orderId);
    };
  }, [stage, orderId]);

  const copy = (value: string | number | null) => {
    if (value === null) return;
    void navigator.clipboard.writeText(String(value));
    toast.success(t('toast.copied'));
  };

  const knownStage = order ? findLegacyOrderStage(order.stage) : undefined;

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        data-legacy
        showClose={false}
        // The source's `noEsc`: Escape must NOT close this drawer.
        onEscapeKeyDown={(event) => event.preventDefault()}
        className="legacy-root legacy-sheet flex h-[92vh] max-w-[1400px] flex-col gap-0 overflow-hidden rounded-[24px] border-0 p-0 sm:max-w-[1400px]"
      >
        {mapExpanded && order ? (
          /*
            The fullscreen-map header (the source's `OrderMapHeader`, shown
            while `withoutTitle` hides the drawer's own): back chevron
            collapses the map, centered stage name over the update stamp,
            close button closes the whole drawer.
          */
          <div className="flex shrink-0 items-center justify-between gap-4 border-b border-[var(--lg-grey-200)] p-4">
            <button
              type="button"
              onClick={() => setMapExpanded(false)}
              aria-label={t('single.collapse_map')}
              className="cursor-pointer rounded-full p-2 text-[var(--lg-fg)] hover:bg-[var(--lg-grey-100)]"
            >
              <ChevronLeftIcon className="size-5" />
            </button>

            <div className="flex min-w-0 flex-col text-center">
              <DialogTitle className="truncate text-[24px] font-normal text-[var(--lg-fg)]">
                {knownStage ? t(knownStage.labelKey) : order.stage}
              </DialogTitle>
              {/* The "в" is the source's hardcoded literal, kept verbatim. */}
              <span className="mt-0.5 text-[14px] font-normal text-[var(--lg-grey-500)]">
                {`${t('single.update')} ${legacyShortDate(new Date())} в ${legacyShortTime(new Date())}`}
              </span>
            </div>

            <button
              type="button"
              onClick={onClose}
              aria-label={t('profile.cancel')}
              className="cursor-pointer rounded-full p-2 text-[var(--lg-grey-500)] hover:bg-[var(--lg-grey-100)]"
            >
              <XIcon className="size-5" />
            </button>
          </div>
        ) : (
          <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[var(--lg-grey-200)] p-6">
            <div className="flex min-w-0 flex-col gap-2">
              <div className="flex flex-wrap items-center gap-3">
                <DialogTitle className="text-[22px] font-semibold text-[var(--lg-fg)]">
                  {order
                    ? `${order.delivery.time ? t('single.order_to') : t('single.order_create')} ${legacyDayMonthTime(order.delivery.time ?? order.created_at ?? '', i18n.language)}`
                    : t('loading')}
                </DialogTitle>
                {/* Desktop badge: SOLID stage fill, white text — the source
                    passes `color="white" withoutOpacity` above 700px. */}
                {order && (
                  <span className="hidden min-[701px]:inline-flex">
                    <LegacyStatusBadge
                      stage={order.stage}
                      solid
                      textColor="var(--lg-surface)"
                    />
                  </span>
                )}
              </div>

              {order && (
                <div className="flex flex-wrap items-center gap-2">
                  <IdChip
                    label={`ID: ${order.display_id}`}
                    onCopy={() => copy(order.display_id)}
                  />
                  {order.vendor_order_id && (
                    <IdChip
                      label={order.vendor_order_id}
                      onCopy={() => copy(order.vendor_order_id)}
                    />
                  )}

                  {/*
                    The public tracking link. Shown whenever the order was
                    created with `send_link`, even before `link` itself arrives —
                    the source renders the pill on the FLAG and only offers the
                    copy affordance once the URL exists, so a merchant can see
                    that a link was requested while it is still being minted.
                    Its wash is one step darker than the id chips' (grey.200).
                  */}
                  {order.delivery.send_link && (
                    <IdChip
                      label={t('single.have_link')}
                      strong
                      {...(order.link ? { onCopy: () => copy(order.link) } : {})}
                    />
                  )}
                </div>
              )}

              {/* Mobile badge, below the chips: solid ONLY at stage 4, where
                  the source flips its text black (`OrderDrawer.tsx:228-236`). */}
              {order && (
                <span className="min-[701px]:hidden">
                  <LegacyStatusBadge
                    stage={order.stage}
                    {...(order.stage === 4
                      ? { solid: true, textColor: 'var(--lg-fg)' }
                      : {})}
                  />
                </span>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {/* Only while the order can still be changed — see
                  `isEditableStage`. The `delivery.edit` gate sits INSIDE the
                  stage condition, as the old drawer's `CheckAccess` did
                  (`OrderDrawer.tsx:243-254`). */}
              {order && isEditableStage(order.stage) && (
                <LegacyCheckAccess permission="delivery.edit">
                  <button
                    type="button"
                    onClick={() =>
                      void navigate({
                        to: '/delivery/orders/$orderId/edit',
                        params: { orderId: String(order.id) },
                        search: { copy: false },
                      })
                    }
                    className="h-[42px] cursor-pointer rounded-[96px] bg-[var(--lg-orange-500)] px-5 text-[16px] font-semibold text-[var(--lg-surface)] transition-opacity hover:opacity-90"
                  >
                    {t('create.edit')}
                  </button>
                </LegacyCheckAccess>
              )}

              <button
                type="button"
                onClick={onClose}
                aria-label={t('profile.cancel')}
                className="cursor-pointer rounded-full p-2 text-[var(--lg-grey-500)] hover:bg-[var(--lg-grey-100)]"
              >
                <XIcon className="size-5" />
              </button>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto bg-[var(--lg-bg)] p-6">
          {isLoading && (
            <div className="flex h-full items-center justify-center text-[var(--lg-grey-500)]">
              {t('loading')}
            </div>
          )}

          {isError && (
            <div className="flex h-full items-center justify-center text-[var(--lg-grey-700)]">
              {t('error.title')}
            </div>
          )}

          {order && mapExpanded && (
            <LegacyOrderMiniMap
              points={points}
              stage={order.stage}
              tracking={effectiveTracking}
              route={route}
              courierPath={courierPath}
              isExpanded
              onToggleExpand={() => setMapExpanded(false)}
              className="h-full min-h-[400px] w-full"
            />
          )}

          {/*
            The source's responsive order (`OrderSingleBox.tsx:307-365`), as
            ONE grid so every section — the map included — is mounted exactly
            once and only its `order`/placement moves:

            - ≥1200px (MUI `lg`): two columns — the info stack on the left,
              the 443px map alongside, stretched to the stack's height.
            - 601–1200px: one column — info stack, then the map (500px tall).
            - ≤600px: the map moves INTO the stack after the address list, and
              the summary cards drop to the bottom.

            The `mt-1`s reproduce the source's 24px outer gap (vs the stack's
            20px) for whichever section sits OUTSIDE the stack at that width.
          */}
          {order && !mapExpanded && (
            <div className="grid grid-cols-1 gap-5 min-[1200px]:grid-cols-[minmax(0,1fr)_443px] min-[1200px]:gap-x-6">
              <div className="order-1 min-w-0">
                <LegacyOrderAddressInfo points={points} />
              </div>

              <div className="order-5 min-w-0 max-[600px]:mt-1 min-[601px]:order-2">
                <LegacyOrderOtherInfo order={order} onPreviewImages={setLightboxImages} />
              </div>

              {cargoPoints.length > 0 && (
                <div className="order-3 min-w-0">
                  <LegacyOrderProducts points={cargoPoints} />
                </div>
              )}

              <div className="order-4 min-w-0">
                <LegacyPriceFormula order={order} />
              </div>

              <LegacyOrderMiniMap
                points={points}
                stage={order.stage}
                tracking={effectiveTracking}
                route={route}
                courierPath={courierPath}
                isExpanded={false}
                onToggleExpand={() => setMapExpanded(true)}
                className={`order-2 w-full max-[1200px]:h-[500px] min-[601px]:order-6 min-[601px]:max-[1200px]:mt-1 min-[1200px]:col-start-2 min-[1200px]:row-start-1 ${
                  cargoPoints.length > 0
                    ? 'min-[1200px]:row-span-4'
                    : 'min-[1200px]:row-span-3'
                }`}
              />
            </div>
          )}
        </div>

        {/*
          The receipt lightbox. Rendered INSIDE the dialog: Radix's modal mode
          disables pointer events outside it, and the content's transform makes
          the lightbox's `fixed inset-0` resolve against the drawer box — so it
          covers the drawer (which is nearly the viewport) and stays clickable
          and inside the focus trap.
        */}
        <LegacyImageLightbox
          images={lightboxImages}
          onClose={() => setLightboxImages(null)}
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * A header pill. Copyable when given `onCopy`, otherwise inert — the
 * tracking-link pill renders before its URL exists, and a button that copies
 * nothing is worse than a label. `strong` is the link pill's darker wash
 * (the source's `grey.200` against the id chips' `grey.100`).
 */
function IdChip({
  label,
  onCopy,
  strong = false,
}: {
  label: string;
  onCopy?: () => void;
  strong?: boolean;
}) {
  const className = `inline-flex items-center gap-1 rounded-[20px] px-2 py-1 text-[16px] font-semibold text-[var(--lg-fg)] ${
    strong ? 'bg-[var(--lg-grey-200)]' : 'bg-[var(--lg-grey-100)]'
  }`;

  if (!onCopy) return <span className={className}>{label}</span>;

  return (
    <button
      type="button"
      onClick={onCopy}
      className={`${className} cursor-pointer hover:bg-[var(--lg-grey-200)]`}
    >
      {label}
      <CopyIcon className="size-3.5" aria-hidden="true" />
    </button>
  );
}
